import { useState } from "react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/App";
import { AlertCircle } from "lucide-react";

export const DisclaimerModal = ({ open, onAccept }) => {
  const { acceptDisclaimer } = useAuth();
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleAccept = async () => {
    if (!agreed) return;
    setLoading(true);
    try {
      await acceptDisclaimer();
      onAccept();
    } catch (error) {
      console.error("Error accepting disclaimer:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={() => {}} dismissible={false}>
      <DrawerContent 
        className="max-h-[95vh] outline-none"
        data-testid="disclaimer-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DrawerHeader className="text-left pb-2">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DrawerTitle className="font-heading text-xl">Important Notice</DrawerTitle>
              <DrawerDescription className="text-sm">
                Please read before continuing
              </DrawerDescription>
            </div>
          </div>
        </DrawerHeader>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          <div className="disclaimer-content">
            <p className="mb-4">
              <strong>LabStack</strong> is designed exclusively for personal research protocol tracking purposes.
            </p>
            
            <p className="mb-4">
              This application:
            </p>
            
            <ul className="list-disc pl-5 mb-4 space-y-2">
              <li>Does <strong>NOT</strong> provide medical advice, diagnosis, or treatment recommendations</li>
              <li>Is <strong>NOT</strong> intended to replace professional medical consultation</li>
              <li>Should <strong>NOT</strong> be used for making health-related decisions</li>
              <li>Is provided for informational and organizational purposes only</li>
            </ul>
            
            <p className="mb-4">
              Always consult with a qualified healthcare professional before starting, changing, or stopping any protocol, medication, or supplement regimen.
            </p>
            
            <p className="text-muted-foreground text-sm mb-4">
              By using this application, you acknowledge that you understand and accept these terms, and that you take full responsibility for your own research activities.
            </p>
          </div>
        </div>

        <DrawerFooter className="pt-2 border-t border-border/50">
          <div className="flex items-start space-x-3 mb-3">
            <Checkbox
              id="agree"
              checked={agreed}
              onCheckedChange={setAgreed}
              className="mt-0.5"
              data-testid="disclaimer-checkbox"
            />
            <label
              htmlFor="agree"
              className="text-sm leading-relaxed cursor-pointer"
            >
              I have read and understand this disclaimer. I acknowledge that LabStack is for personal research tracking only and does not provide medical advice.
            </label>
          </div>
          
          <Button
            onClick={handleAccept}
            disabled={!agreed || loading}
            className="w-full h-12 rounded-full font-semibold"
            data-testid="disclaimer-accept-btn"
          >
            {loading ? "Processing..." : "I Understand & Accept"}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default DisclaimerModal;
